import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import ErrorComponent from '../../../../new-components/ui/ErrorComponent';
import { getCompoundSummary } from '../../../../new-lib/apis';
import CompoundInfo from './CompoundInfo';
import CoverSection from './CoverSection';
import PriceInfo from './PriceInfo';
import AvailableProperties from './available-properties/AvailableProperties';
import NawyLoadingIndicator from '../../../../new-components/NawyLoadingIndicator';
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { logEvent } from '../../../../new-lib/analytics';
import { getPageLocation } from '../../../../helpers/common';
import LaunchesBannerContainer from '../../../entity/launch/components/LaunchesBanner/LaunchesBannerContainer';
import useCompoundLaunches from '../../hooks/CompoundLaunches';
import { CompoundSummaryDto } from '../../dtos';

interface CompoundDrawerProps {
  snapPoint?: number;
}

const CompoundDrawer = ({ snapPoint }: CompoundDrawerProps) => {
  const { locale, asPath } = useRouter();

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const {
    data: compound,
    isLoading,
    isError
  } = useQuery<CompoundSummaryDto>({
    queryKey: [`compound-${selectedCompound?.id}`, selectedCompound, locale],
    queryFn: () => getCompoundSummary(selectedCompound.id, locale)
  });
  const launches = useCompoundLaunches(selectedCompound?.id);
  const renderCompound = useCallback(() => {
    if (isLoading && isMobile) {
      return (
        <div className="loader">
          <NawyLoadingIndicator />
        </div>
      );
    }
    if (isError) {
      return <ErrorComponent />;
    }

    return compound ? (
      <>
        {snapPoint != 1 && snapPoint != 2 && (
          <CoverSection
            image={compound?.imageUrl}
            location={compound?.areaName}
          />
        )}

        <CompoundInfo
          compoundName={compound?.name}
          developerLogo={compound?.developerLogoUrl}
          developerSalePrice={compound?.prices?.developerStartingPrice}
          resalePrice={compound?.prices?.resaleStartingPrice}
        />

        {!isMobile &&
          (compound?.prices?.developerStartingPrice > 0 ||
            compound?.prices?.resaleStartingPrice > 0) && (
            <PriceInfo
              developerSalePrice={compound?.prices?.developerStartingPrice}
              resalePrice={compound?.prices?.resaleStartingPrice}
              currency={compound?.prices?.currency}
            />
          )}

        {launches && (
          <LaunchesBannerContainer
            launches={launches.launches}
            imageSize="medium"
          />
        )}

        <AvailableProperties compound={compound} />
      </>
    ) : null;
  }, [compound, isLoading, isError, snapPoint]);

  useEffect(() => {
    logEvent.click('map_compound_view', getPageLocation(asPath), 'map', {
      cmpnd_id: compound?.id,
      cmpnd_name: compound?.name,
      area_id: compound?.areaId,
      area_name: compound?.areaName
    });
  }, []);

  return <Container className="hide-scrollbar">{renderCompound()}</Container>;
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: center;

  .cta {
    width: 100%;
    background-color: white;
    z-index: 99;
    position: sticky;
    padding: 16px 0;
    bottom: 0;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    width: 100%;

    .cta {
      position: fixed;
      padding: 16px;
    }

    .loader {
      margin-top: 100px;
    }
  }
`;

export default CompoundDrawer;
